import { Check } from "lucide-react";
import { CtaAnchor } from "@/src/common/components/cta-link";
import { getSignUpLink } from "@/src/common/constants/app-links";
import { SITE_ROUTES } from "@/src/common/constants/site-routes";

export type PricingTone = "default" | "highlighted" | "premium";

interface PricingTierCardProps {
	id: "free" | "pro" | "premium";
	name: string;
	tagline: string;
	price: string;
	period?: string;
	description: string;
	features: string[];
	cta: string;
	footnote?: string;
	badge?: string;
	tone: PricingTone;
}

export function PricingTierCard({
	id,
	name,
	tagline,
	price,
	period,
	description,
	features,
	cta,
	footnote,
	badge,
	tone,
}: PricingTierCardProps) {
	const isHighlighted = tone === "highlighted";
	const isPremium = tone === "premium";
	const isDefault = tone === "default";

	const cardClass = isHighlighted
		? "relative border-2 border-brand-500 bg-white shadow-lift"
		: isPremium
			? "relative border border-transparent bg-gradient-to-b from-brand-900 to-brand-800 text-white shadow-lift"
			: "relative border border-default-200 bg-white shadow-card";

	const ctaHref = getSignUpLink(id);
	const isContactSales = id === "premium";

	return (
		<article id={id === "premium" ? "premium" : undefined} className={`flex flex-col gap-6 rounded-3xl p-8 ${cardClass}`}>
			{badge ? (
				<span className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center rounded-full bg-brand-gradient px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow">
					{badge}
				</span>
			) : null}

			<header className="flex flex-col gap-2">
				<p className={`text-xs font-bold uppercase tracking-[0.16em] ${isPremium ? "text-white/80" : "text-brand-700"}`}>{tagline}</p>
				<h3 className={`text-2xl font-bold ${isPremium ? "text-white" : "text-foreground"}`}>{name}</h3>
				<p className={`text-sm ${isPremium ? "text-white/70" : "text-default-600"}`}>{description}</p>
			</header>

			<div className="flex items-baseline gap-2">
				<span className={`text-5xl font-bold tracking-tight ${isPremium ? "text-white" : "text-foreground"}`}>{price}</span>
				{period ? <span className={`text-sm ${isPremium ? "text-white/70" : "text-default-500"}`}>{period}</span> : null}
			</div>

			{isContactSales ? (
				<CtaAnchor href={`${SITE_ROUTES.CONTACT}?intent=premium`} variant="white" size="md" className="w-full">
					{cta}
				</CtaAnchor>
			) : (
				<CtaAnchor href={ctaHref} variant={isHighlighted ? "gradient" : "outline"} size="md" className="w-full">
					{cta}
				</CtaAnchor>
			)}

			<ul className="space-y-2.5 text-sm">
				{features.map((feat) => (
					<li key={feat} className="flex items-start gap-2.5">
						<span
							className={`mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full ${
								isPremium ? "bg-white/15 text-white" : "bg-brand-100 text-brand-700"
							}`}
						>
							<Check className="h-3 w-3" />
						</span>
						<span className={isPremium ? "text-white/80" : "text-default-700"}>{feat}</span>
					</li>
				))}
			</ul>

			{footnote ? <p className={`mt-auto text-xs ${isPremium ? "text-white/60" : "text-default-500"}`}>{footnote}</p> : null}

			<span className="sr-only">{isDefault ? "default" : ""}</span>
		</article>
	);
}
